// UI chrome strings (namespaced) shared across the app shell: marketing header +
// footer, dashboard sidebar/topbar, admin sidebar/topbar. Page BODIES are not
// here yet — they translate incrementally, each adding its own namespace. Keep
// en and ar structurally identical (the parity test enforces it).
import { useLocale } from './useLocale'

const UI = {
  en: {
    common: { skipToMain: 'Skip to main content' },
    nav: {
      solutions: 'Solutions',
      resources: 'Resources',
      pricing: 'Pricing',
      contact: 'Contact',
      login: 'Login',
      getStarted: 'Get started',
      openMenu: 'Open navigation menu',
      howItWorks: 'How it works',
      faq: 'FAQ',
      documentation: 'Documentation',
      support: 'Support',
      faqPage: 'FAQ page',
    },
    solutionMenu: {
      'property-managers': { title: 'Property Managers', desc: 'Automate deposits across your PMS or channel manager.' },
      healthcare: { title: 'Healthcare', desc: 'Secure out-of-pocket costs and cut overdue balances.' },
      'vehicle-rental': { title: 'Vehicle Rental', desc: 'Protect your fleet and speed up the counter.' },
      'equipment-rental': { title: 'Equipment Rental', desc: 'Digitize deposits so crews collect gear faster.' },
    },
    resourceMenu: {
      documentation: { title: 'Documentation', desc: 'Guides and API reference.' },
      webhooks: { title: 'Webhooks', desc: 'Real-time event delivery.' },
      support: { title: 'Support', desc: 'Help center and contact options.' },
      faq: { title: 'FAQ', desc: 'Common questions, answered.' },
    },
    footer: {
      tagline: 'Security deposits made simple.',
      product: 'Product',
      resources: 'Resources',
      connect: 'Connect',
      solutions: 'Solutions',
      howCardHolds: 'How card holds work',
      depositFree: 'Deposit-free renting',
      pricing: 'Pricing',
      joinWaitlist: 'Join the waitlist',
      faq: 'FAQ',
      blog: 'Blog',
      documentation: 'Documentation',
      support: 'Support',
      privacyPolicy: 'Privacy policy',
      terms: 'Terms',
      contactUs: 'Contact Us',
      rights: '© 2026 Akara Portal LLC. All rights reserved.',
      privacy: 'Privacy',
    },
    dash: {
      home: 'Home',
      operations: 'Operations',
      requests: 'Requests',
      claims: 'Claims',
      insights: 'Insights',
      reports: 'Reports',
      platform: 'Platform',
      integrations: 'Integrations',
      webhooks: 'Webhooks',
      billing: 'Billing & licensing',
      notifications: 'Notifications',
      settings: 'Settings',
      account: 'Account',
      payouts: 'Payouts',
      security: 'Password & security',
      adminPanel: 'Admin panel',
      newRequest: 'New request',
      signOut: 'Sign out',
      support: 'Support',
      greetingMorning: 'Good morning',
      greetingAfternoon: 'Good afternoon',
      greetingEvening: 'Good evening',
      meta: {
        requests: { title: 'Deposit requests', description: 'Create new requests and track deposit status in real time.' },
        claims: { title: 'Claims', description: 'Review submitted claims and follow their approval status.' },
        reports: { title: 'Reports', description: 'Export CSV reports for reconciliation and audits.' },
        integrations: { title: 'Integrations', description: 'Connect your PMS or ERP and sync deposit workflows.' },
        webhooks: { title: 'Webhooks', description: 'Outbound webhooks and inbound PMS API.' },
        payouts: { title: 'Payouts', description: 'Manage how claimed deposits reach your business.' },
        security: { title: 'Password & security', description: 'Password, two-factor authentication, and sign-in options.' },
        account: { title: 'Account', description: 'Manage your company profile and contact information.' },
        billing: { title: 'Billing & licensing', description: 'Your plan, payment details, and invoices.' },
        notifications: { title: 'Notifications', description: 'Choose what Akara emails you about.' },
      },
    },
    solution: {
      iAm: (persona) => `I am ${persona}`,
      getStartedFree: 'Get started free',
      talkToSales: 'Talk to sales',
      overview: 'Overview',
      problemToday: 'The problem today',
      whatDepositsCost: 'What deposits cost you before Akara',
      whyTeams: 'Why teams choose Akara',
      howItWorks: 'How it works',
      step: (n) => `Step ${n}`,
      faqTitle: 'Frequently asked questions',
      ctaSub: 'Get started in minutes — no commitment required.',
    },
    faqPage: {
      label: 'FAQ',
      title: 'Frequently asked questions',
      subtitle:
        'Quick answers to common questions about deposits, workflows, and security.',
      stillTitle: 'Still have questions?',
      stillText: 'Our team is happy to walk you through anything.',
      contactSupport: 'Contact support',
    },
    support: {
      label: 'Support',
      title: 'We’re here to help',
      subtitle:
        'Our team is ready to help you launch faster, troubleshoot issues, and optimize your deposit workflows.',
      liveChatTitle: 'Live chat',
      liveChatBody: 'Reach our support team Monday to Friday, 9 am – 6 pm (GMT+4).',
      emailTitle: 'Email support',
      emailBefore: 'Send details to ',
      emailAfter: ' and we’ll respond within 1 business day.',
      disputesTitle: 'Disputes and payment issues',
      disputesBefore: 'For deposit disputes or payment problems, email ',
      disputesMid:
        '. For chargebacks or payment disputes we may escalate to our payment provider. See the ',
      disputesLink: 'Escalation',
      disputesAfter: ' section below.',
      incidentTitle: 'Incident response',
      incidentBody: 'Priority response for critical payment or availability issues.',
      onboardingTitle: 'Onboarding',
      onboardingBody: 'Guided setup sessions for teams switching from manual deposits.',
      escalationTitle: 'Escalation to our payment provider',
      escalationP1: 'We use a third-party payment provider for payment processing. We may escalate to them for ',
      escalationChargebacks: 'chargebacks',
      escalationP2: ' (cardholder disputes a charge with their bank) or ',
      escalationDisputes: 'payment disputes',
      escalationP3: ' (a payment is flagged for review). In those cases we follow the provider’s process; you may be asked to provide evidence (e.g. authorization confirmation, deposit terms, booking records). Responding by any deadline we give you helps us resolve the case.',
      ctaTitle: 'Need to review product details first?',
      ctaText: 'Our documentation covers setup, integrations, and best practices.',
      ctaBtn: 'Visit documentation',
    },
    contact: {
      label: 'Contact',
      title: 'Get in touch with us',
      subtitle:
        'We’re happy to help. Reach our team for onboarding, product questions, or support.',
      uae: 'United Arab Emirates',
      callUs: 'Call us: ',
      emailUs: 'Email us: ',
      viewSupport: 'View support resources',
      sendTitle: 'Send us a message',
      sendText: 'Tell us what you need and our team will get back within 1 business day.',
      fullName: 'Full name',
      companyName: 'Company name',
      workEmail: 'Work email',
      message: 'Message',
      sendBtn: 'Send message',
      officeLocations: 'Office locations',
      dubaiHq: 'Dubai (HQ)',
      dubaiAddr: 'Dubai, United Arab Emirates',
      preferBefore: 'Prefer email? Write to ',
      preferAfter: '.',
    },
    pricing: {
      label: 'Pricing',
      title: 'Pricing built for modern teams',
      subtitle:
        'Start free, then upgrade as you grow. You only pay a small fee when you claim a deposit — released deposits are always free.',
      chooseTitle: 'Choose the plan that fits your volume',
      chooseSub: 'Every plan includes automated requests, secure payment data, and audit trails.',
      loadingPlans: 'Loading plans…',
      mostPopular: 'Most popular',
      yourPlan: 'Your plan',
      letsTalk: 'Let’s talk',
      volumeBased: 'Volume-based pricing · your lowest per-claim rate',
      free: 'Free',
      noMonthlyFee: (pct) => `No monthly fee · ${pct} per claimed deposit`,
      perMonth: (pct) => `per month · ${pct} per claimed deposit`,
      // CTAs
      talkToSales: 'Talk to sales',
      getStartedFree: 'Get started free',
      getStarted: 'Get started',
      currentPlan: 'Current plan',
      requested: 'Requested',
      requesting: 'Requesting…',
      requestPlan: 'Request plan',
      requestedToast: (name) => `Requested the ${name} plan. Our team will confirm shortly.`,
      requestError: 'Could not send your plan request.',
      // Feature bullets
      featClaimFee: (pct) => `${pct} platform fee per claimed deposit`,
      featAutomated: 'Automated requests & reminders',
      featUpTo: (money) => `Up to ${money} per deposit`,
      featNoLimit: 'No per-deposit limit',
      featEmail: 'Email notifications',
      featSms: 'SMS notifications',
      featFraud: 'Fraud monitoring & audit trails',
      featBranding: 'Custom branding',
      featApi: 'API access + webhooks',
      featOnboarding: 'Dedicated onboarding & SLA',
      featLowestRate: 'Your lowest per-claim rate',
      // Transparency
      transparentLabel: 'Transparent pricing',
      transparentTitle: 'See exactly what you pay',
      transparentSub:
        'No hidden fees. You pay your monthly plan and a per-claim fee only when you keep a deposit — nothing when you release one.',
      comparedTitle: 'Compared to traditional deposit handling',
      traditionalTitle: 'Traditional methods',
      traditionalItems: [
        'Cash held by agents or property managers.',
        'Manual refunds and reconciliation.',
        'Disputes often handled offline.',
        'Low transparency for tenants.',
      ],
      akaraItems: [
        'Digital deposits secured without blocking funds.',
        'Automated release workflows.',
        'Audit-ready reporting and compliance logs.',
        'Funds settle directly to your own connected payout account.',
      ],
      // Mechanics
      mech1Title: 'How pricing works',
      mech1Body:
        'You pay a monthly plan for your tier, plus a small platform fee on each deposit you claim. Release a deposit and there is no fee at all.',
      mech2Title: 'Lower rates as you grow',
      mech2Body:
        'Higher tiers cut your per-claim fee — from 3.9% on Starter down to your lowest negotiated rate on Custom — and raise your monthly volume.',
      mech3Title: 'What’s included',
      mech3Body:
        'Every plan includes automated reminders, fraud monitoring, and audit trails that simplify disputes.',
      ctaTitle: 'Need a custom quote?',
      ctaText: 'Reach out and we’ll build a plan for your specific volume and workflow.',
    },
    auth: {
      needHelp: 'Need help?',
      emailAddress: 'Email address',
      password: 'Password',
      or: 'or',
      redirecting: 'Redirecting…',
      opening: 'Opening…',
      googleSignIn: 'Sign in with Google',
      googleSignUp: 'Sign up with Google',
      appleSignIn: 'Sign in with Apple',
      appleSignUp: 'Sign up with Apple',
      holdShow: 'Hold to show password',
      releaseHide: 'Release to hide password',
      showcase: {
        signin: { eyebrow: 'Merchant portal', title: 'Welcome back to your operations cockpit', description: 'Track deposit requests, approve actions faster, and keep your customer journey smooth from one dashboard.' },
        register: { eyebrow: 'Get started', title: 'Launch your deposit workflows in minutes', description: 'Create your merchant account and start sending secure, branded requests with real-time status visibility.' },
        forgot: { eyebrow: 'Account recovery', title: 'Regain access securely', description: 'Reset your password quickly and continue managing your requests without losing momentum.' },
        verify: { eyebrow: 'Verification', title: 'One step left to activate your account', description: 'Confirm your email to secure your account and unlock the full merchant workspace.' },
        'forgot-sent': { eyebrow: 'Email sent', title: 'Check your inbox for the reset link', description: 'We sent password reset instructions so you can get back in and continue where you left off.' },
      },
      showcasePoints: ['Real-time deposit status', 'Fewer disputes and follow-ups', 'Fast setup for UAE teams'],
      tabSignIn: 'Sign in',
      tabCreate: 'Create account',
      welcomeBack: 'Welcome back',
      signinSub: 'Sign in to access your merchant dashboard and manage deposits.',
      signinBtn: 'Sign in',
      signingIn: 'Signing in...',
      forgotLink: 'Forgot your password?',
      enterPassword: 'Enter your password',
      createTitle: 'Create your account',
      createSub: 'Register with your email to get started. We’ll send a verification link to confirm your address.',
      confirmPassword: 'Confirm password',
      min6: 'Minimum 6 characters',
      reenter: 'Re-enter your password',
      agreeTermsBefore: 'I agree to Akara’s ',
      termsLink: 'Terms and Conditions of Use',
      agreeTermsAfter: '.',
      agreePrivacyBefore: 'I have read and understood Akara’s ',
      privacyLink: 'Personal Data Protection Policy',
      agreePrivacyAfter: '.',
      createBtn: 'Create account',
      creating: 'Creating account...',
      checkEmailTitle: 'Check your email',
      checkEmailBefore: 'We’ve sent a verification link to ',
      checkEmailAfter: '. Click the link in your email to verify your account and complete your profile setup.',
      backToSignin: 'Back to sign in',
      didntBefore: 'Didn’t receive it? Check spam or ',
      tryAgain: 'try again',
      didntAfter: '.',
      resetTitle: 'Reset your password',
      resetSub: 'Enter your email and we’ll send you a link to reset your password.',
      sendResetBtn: 'Send reset link',
      sending: 'Sending...',
      resetSentTitle: 'Reset link sent',
      resetSentBefore: 'We’ve sent a password reset link to ',
      resetSentAfter: '. Click the link in your email to set a new password.',
      errEmailPassword: 'Please enter your email and password.',
      errPasswordMin: 'Password must be at least 6 characters.',
      errPasswordMatch: 'Passwords do not match.',
      errAcceptTerms: 'Please accept the Terms and Conditions and Privacy Policy.',
      errEmail: 'Please enter your email address.',
      // ResetPassword page
      setNewTitle: 'Set new password',
      setNewSub: 'Choose a new password for your account.',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      updateBtn: 'Update password',
      updating: 'Updating...',
      passwordUpdatedTitle: 'Password updated',
      passwordUpdatedBody: 'Your password has been reset successfully. Redirecting to your dashboard…',
    },
    getStarted: {
      headerTitle: 'Get started free',
      headerSub: 'Tell us about your business so we can set up your merchant account.',
      successTitle: 'Thanks! We’ll be in touch shortly.',
      successBody: 'Our team will review your details and contact you within 1 business day. If you already have an account, sign in to continue.',
      signIn: 'Sign in',
      submitAnother: 'Submit another response',
      verifyTitle: 'Verify your phone number',
      verifyBefore: 'We sent a 6-digit code to ',
      verifyAfter: '. Enter it below to finish setting up your merchant account.',
      verifyCodeLabel: 'Verification code',
      verifyBtn: 'Verify and continue',
      verifying: 'Verifying...',
      resendBtn: 'Resend code',
      resending: 'Resending...',
      resendIn: (s) => `Resend code in ${s}s`,
      lobLabel: 'My line of business',
      lobPlaceholder: 'Select a line of business',
      lobPropertyManagement: 'Property management',
      lobVehicleRental: 'Vehicle rental',
      lobEquipmentRental: 'Equipment rental',
      lobHealthcare: 'Healthcare',
      lobOther: 'Other',
      currencyLabel: 'Currency of my account',
      businessNameLabel: 'My business name',
      websiteLabel: 'My Website',
      countryLabel: 'Country',
      countryUae: 'United Arab Emirates',
      countrySaudi: 'Saudi Arabia',
      countryQatar: 'Qatar',
      countryKuwait: 'Kuwait',
      cityLabel: 'City',
      firstNameLabel: 'First Name',
      lastNameLabel: 'Last Name',
      emailLabel: 'My email address',
      phoneLabel: 'My phone number',
      promoLabel: 'Promotional code',
      agreeTermsBefore: 'I accept Akara’s ',
      termsLink: 'Terms and Conditions of Use',
      agreeTermsAfter: '.',
      agreePrivacyBefore: 'I have read and understood Akara’s ',
      privacyLink: 'personal data protection policy',
      agreePrivacyAfter: '.',
      submitBtn: 'Complete registration',
      saving: 'Saving...',
      errAcceptTerms: 'Please accept the Terms and Conditions and Privacy Policy.',
      errOtp: 'Enter the code we sent to your phone.',
      errMissing: (label) => `Please enter your ${label}.`,
      fieldBusinessName: 'business name',
      fieldCity: 'city',
      fieldFirstName: 'first name',
      fieldLastName: 'last name',
      fieldEmail: 'email address',
      fieldPhone: 'phone number',
    },
    waitlist: {
      brandEyebrow: 'Join the waitlist',
      brandTitle: 'Deposits, finally effortless.',
      depositHeld: 'Deposit held',
      active: 'Active',
      releases: 'Releases in 3 days, not 10',
      stepIndustry: 'Your industry',
      stepVolume: 'Deposit volume',
      stepCompany: 'Business details',
      stepContact: 'Contact info',
      stepReview: 'Review & join',
      industries: {
        shortterm: { label: 'Short-term Rentals', desc: 'Airbnb, Vrbo, and direct bookings' },
        longterm: { label: 'Landlord & long-term lets', desc: 'Residential tenancy deposits' },
        car: { label: 'Car & vehicle rental', desc: 'Daily and weekly hire fleets' },
        equipment: { label: 'Equipment rental', desc: 'Tools, gear, and machinery' },
        healthcare: { label: 'Healthcare', desc: 'Medical and dental deposits' },
        other: { label: 'Other', desc: 'Any business that takes deposits' },
      },
      metricLabels: {
        'Number of listings': 'Number of listings',
        'Average deposit held': 'Average deposit held',
        'Frequency of bookings': 'Frequency of bookings',
        'Units managed': 'Units managed',
        'Average deposit per tenancy': 'Average deposit per tenancy',
        'Typical tenancy length': 'Typical tenancy length',
        'Fleet size': 'Fleet size',
        'Average deposit per rental': 'Average deposit per rental',
        'Typical rental length': 'Typical rental length',
        'Items in inventory': 'Items in inventory',
        'Average deposit per item': 'Average deposit per item',
        'Typical rental frequency': 'Typical rental frequency',
        'Patients per month': 'Patients per month',
        'Average deposit / copay held': 'Average deposit / copay held',
        'Deposit release trigger': 'Deposit release trigger',
      },
      options: {
        'Under 10 / month': 'Under 10 / month', '10–25 / month': '10–25 / month', '26–50 / month': '26–50 / month', '51–100 / month': '51–100 / month', '100+ / month': '100+ / month',
        'Month-to-month': 'Month-to-month', '6 months': '6 months', '12 months': '12 months', '24+ months': '24+ months',
        '1–2 days': '1–2 days', '3–7 days': '3–7 days', '1–4 weeks': '1–4 weeks', '1+ month': '1+ month',
        Daily: 'Daily', Weekly: 'Weekly', Monthly: 'Monthly',
        'Visit complete': 'Visit complete', 'Insurance clears': 'Insurance clears', 'Manual review': 'Manual review',
      },
      countries: {
        'United Arab Emirates': 'United Arab Emirates', 'Saudi Arabia': 'Saudi Arabia', Qatar: 'Qatar', Kuwait: 'Kuwait', Bahrain: 'Bahrain', Oman: 'Oman',
      },
      selectOption: 'Select an option',
      industryTitle: 'What kind of deposits do you take?',
      industrySub: 'Pick the one that fits your business best.',
      volumeTitle: 'Tell us about your volume',
      volumeSub: 'Rough estimates are fine — skip anything you’re unsure of.',
      companyTitle: 'About your business',
      companySub: 'So we can set up the right account when your invite lands.',
      businessName: 'Business name',
      website: 'Website',
      optional: '(optional)',
      country: 'Country',
      city: 'City',
      contactTitle: 'Where should we send your invite?',
      contactSub: 'We’ll only email you about early access — no spam.',
      fullName: 'Full name',
      workEmail: 'Work email',
      emailError: 'Enter a valid email like name@company.com',
      phone: 'Phone',
      reviewTitle: 'Look right?',
      reviewSub: 'Confirm your details and secure your spot on the waitlist.',
      rowIndustry: 'Industry',
      rowBusiness: 'Business',
      rowLocation: 'Location',
      rowContact: 'Contact',
      rowEmail: 'Email',
      dash: '—',
      there: 'there',
      doneTitle: (n) => `You’re on the list, ${n}.`,
      doneBefore: 'We’ll email ',
      doneAfter: (industry) => ` the moment early access opens for ${industry} businesses.`,
      addAnother: 'Add another business',
      back: 'Back',
      continueBtn: 'Continue',
      join: 'Join the waitlist',
      joining: 'Joining…',
      submitError: 'Something went wrong saving your details. Please try again.',
    },
    onboarding: {
      back: 'Back',
      stepOf: (n, total) => `Step ${n} of ${total}`,
      continueBtn: 'Continue',
      finish: 'Finish setup',
      saving: 'Saving…',
      loading: 'Loading…',
      selectOption: 'Select an option',
      welcomeTitle: 'Let’s set up your Akara workspace',
      welcomeSub: 'Tell us about your business and we’ll configure a dashboard that fits how you take deposits — takes about 3 minutes.',
      proof1: 'Funds pre-authorized, never blocked',
      proof2: 'Released in days, not weeks',
      proof3: 'Every deposit tracked and documented',
      getStarted: 'Get started',
      personaTitle: 'Who’s setting this up?',
      personaSub: 'This shapes the fields we ask for next.',
      businessTitle: 'Business',
      businessDesc: 'A company managing deposits across properties, fleets, or clients',
      individualTitle: 'Individual',
      individualDesc: 'A landlord or host managing deposits on your own',
      industriesTitle: 'Which industries do you take deposits for?',
      industriesSub: 'Select all that apply — this tailors your dashboard and default workflows.',
      metricsTitle: 'Tell us about your volume',
      metricsSub: 'Rough estimates are fine — this just sets sensible defaults. You can skip anything.',
      profileTitle: 'A few details about you',
      profileSub: 'We’ll use this to set up your account and send deposit notifications.',
      fullName: 'Full name',
      workEmail: 'Work email',
      emailError: 'Enter a valid email address (e.g. name@company.com)',
      companyName: 'Company name',
      verifyTitle: 'Check your inbox',
      verifyBefore: 'We sent a 6-digit code to ',
      yourEmail: 'your email',
      resendCode: 'Resend code',
      resending: 'Resending…',
      codeResent: 'Code resent.',
      dashboardTitle: 'Configure your dashboard',
      dashboardSub: 'Common defaults are already on — adjust anything before you land in Akara.',
      notifications: 'Notifications',
      integrations: 'Integrations',
      currencyDefaults: 'Currency & deposit defaults',
      defaultCurrency: 'Default currency',
      defaultDeposit: 'Default deposit amount',
      successTitle: (name) => `You’re all set, ${name}`,
      successBefore: 'Your workspace for ',
      yourCompany: 'your company',
      successAfter: ' is configured. Here’s what we set up:',
      accountType: 'Account type',
      industriesRow: 'Industries',
      noneSelected: 'None selected',
      goToDashboard: 'Go to dashboard',
      there: 'there',
      personaBusiness: 'Business',
      personaIndividual: 'Individual',
      dash: '—',
      errSaveDetails: 'Could not save your details.',
      errSaveNotif: 'Could not save notification preferences.',
      errSaveDefaults: 'Could not save your dashboard defaults.',
      industries: {
        shortterm: { label: 'Holiday homes & short-term rentals', desc: 'Airbnb, Vrbo, and direct bookings' },
        longterm: { label: 'Landlord & tenant', desc: 'Long-term residential leases' },
        car: { label: 'Car rental', desc: 'Daily and weekly vehicle hire' },
        equipment: { label: 'Equipment rental', desc: 'Tools, gear, and machinery' },
        healthcare: { label: 'Healthcare', desc: 'Medical and dental deposits' },
      },
      metricLabels: {
        'Number of listings': 'Number of listings',
        'Average nightly deposit': 'Average nightly deposit',
        'Primary booking platform': 'Primary booking platform',
        'Number of units managed': 'Number of units managed',
        'Average deposit per tenancy': 'Average deposit per tenancy',
        'Average tenancy length': 'Average tenancy length',
        'Fleet size': 'Fleet size',
        'Average deposit per rental': 'Average deposit per rental',
        'Typical rental length': 'Typical rental length',
        'Items in inventory': 'Items in inventory',
        'Average deposit per item': 'Average deposit per item',
        'Typical rental frequency': 'Typical rental frequency',
        'Patients per month': 'Patients per month',
        'Average deposit / copay held': 'Average deposit / copay held',
        'Deposit release trigger': 'Deposit release trigger',
      },
      options: {
        Airbnb: 'Airbnb', 'Booking.com': 'Booking.com', Vrbo: 'Vrbo', 'Direct / own site': 'Direct / own site', Other: 'Other',
        'Month-to-month': 'Month-to-month', '6 months': '6 months', '12 months': '12 months', '24+ months': '24+ months',
        '1-2 days': '1-2 days', '3-7 days': '3-7 days', '1-4 weeks': '1-4 weeks', '1+ month': '1+ month',
        Daily: 'Daily', Weekly: 'Weekly', Monthly: 'Monthly',
        'Visit complete': 'Visit complete', 'Insurance clears': 'Insurance clears', 'Manual review': 'Manual review',
      },
      notif: {
        received: { label: 'Deposit received', desc: 'When a new deposit is secured' },
        nearing: { label: 'Deposit nearing release', desc: '3 days before scheduled release' },
        dispute: { label: 'Dispute opened', desc: 'When a claim or dispute is filed' },
        weekly: { label: 'Weekly summary email', desc: 'A digest of activity every Monday' },
      },
      integrationLater: 'I’ll connect later',
    },
    admin: {
      badge: 'Admin panel',
      overview: 'Overview',
      platform: 'Platform',
      merchants: 'Merchants',
      deposits: 'Deposits',
      claims: 'Claims',
      escalations: 'Escalations',
      signOut: 'Sign out',
      switchToMerchant: 'Switch to merchant view',
      superAdmin: 'Super admin',
      meta: {
        users: { title: 'Merchants', description: 'All registered merchants and their activity.' },
        deposits: { title: 'Deposits', description: 'Recent deposit requests across all merchants.' },
        claims: { title: 'Claims', description: 'Review and action claims submitted by merchants.' },
        home: { title: 'Admin overview', description: 'Platform-wide snapshot of merchants, deposits, and claims.' },
      },
    },
  },
  ar: {
    common: { skipToMain: 'تخطَّ إلى المحتوى الرئيسي' },
    nav: {
      solutions: 'الحلول',
      resources: 'الموارد',
      pricing: 'الأسعار',
      contact: 'تواصل معنا',
      login: 'تسجيل الدخول',
      getStarted: 'ابدأ الآن',
      openMenu: 'فتح قائمة التنقل',
      howItWorks: 'كيف يعمل',
      faq: 'الأسئلة الشائعة',
      documentation: 'التوثيق',
      support: 'الدعم',
      faqPage: 'صفحة الأسئلة الشائعة',
    },
    solutionMenu: {
      'property-managers': { title: 'مديرو العقارات', desc: 'أتمتة الودائع عبر نظام إدارة العقارات أو مدير القنوات.' },
      healthcare: { title: 'الرعاية الصحية', desc: 'تأمين التكاليف الشخصية وتقليل المبالغ المتأخرة.' },
      'vehicle-rental': { title: 'تأجير المركبات', desc: 'احمِ أسطولك وسرّع إجراءات التسليم.' },
      'equipment-rental': { title: 'تأجير المعدات', desc: 'رقمنة الودائع ليستلم الفريق المعدات بسرعة.' },
    },
    resourceMenu: {
      documentation: { title: 'التوثيق', desc: 'الأدلة ومرجع واجهة البرمجة.' },
      webhooks: { title: 'الويب هوك', desc: 'تسليم الأحداث في الوقت الفعلي.' },
      support: { title: 'الدعم', desc: 'مركز المساعدة وخيارات التواصل.' },
      faq: { title: 'الأسئلة الشائعة', desc: 'إجابات على الأسئلة المتكررة.' },
    },
    footer: {
      tagline: 'الودائع الأمنية بكل بساطة.',
      product: 'المنتج',
      resources: 'الموارد',
      connect: 'تواصل',
      solutions: 'الحلول',
      howCardHolds: 'كيف تعمل حجوزات البطاقة',
      depositFree: 'الإيجار بدون وديعة',
      pricing: 'الأسعار',
      joinWaitlist: 'انضم إلى قائمة الانتظار',
      faq: 'الأسئلة الشائعة',
      blog: 'المدونة',
      documentation: 'التوثيق',
      support: 'الدعم',
      privacyPolicy: 'سياسة الخصوصية',
      terms: 'الشروط',
      contactUs: 'تواصل معنا',
      rights: '© 2026 Akara Portal LLC. جميع الحقوق محفوظة.',
      privacy: 'الخصوصية',
    },
    dash: {
      home: 'الرئيسية',
      operations: 'العمليات',
      requests: 'الطلبات',
      claims: 'المطالبات',
      insights: 'التحليلات',
      reports: 'التقارير',
      platform: 'المنصة',
      integrations: 'التكاملات',
      webhooks: 'الويب هوك',
      billing: 'الفوترة والتراخيص',
      notifications: 'الإشعارات',
      settings: 'الإعدادات',
      account: 'الحساب',
      payouts: 'المدفوعات',
      security: 'كلمة المرور والأمان',
      adminPanel: 'لوحة الإدارة',
      newRequest: 'طلب جديد',
      signOut: 'تسجيل الخروج',
      support: 'الدعم',
      greetingMorning: 'صباح الخير',
      greetingAfternoon: 'مساء الخير',
      greetingEvening: 'مساء الخير',
      meta: {
        requests: { title: 'طلبات الوديعة', description: 'أنشئ طلبات جديدة وتتبّع حالة الوديعة في الوقت الفعلي.' },
        claims: { title: 'المطالبات', description: 'راجع المطالبات المقدَّمة وتابع حالة الموافقة عليها.' },
        reports: { title: 'التقارير', description: 'صدّر تقارير CSV للتسوية والتدقيق.' },
        integrations: { title: 'التكاملات', description: 'اربط نظام إدارة العقارات أو تخطيط الموارد وزامن سير عمل الودائع.' },
        webhooks: { title: 'الويب هوك', description: 'ويب هوك صادر وواجهة برمجة واردة لنظام إدارة العقارات.' },
        payouts: { title: 'المدفوعات', description: 'تحكّم في كيفية وصول الودائع المطالَب بها إلى نشاطك.' },
        security: { title: 'كلمة المرور والأمان', description: 'كلمة المرور والمصادقة الثنائية وخيارات تسجيل الدخول.' },
        account: { title: 'الحساب', description: 'أدر ملف شركتك ومعلومات التواصل.' },
        billing: { title: 'الفوترة والتراخيص', description: 'خطتك وتفاصيل الدفع والفواتير.' },
        notifications: { title: 'الإشعارات', description: 'اختر ما تراسلك Akara بشأنه عبر البريد الإلكتروني.' },
      },
    },
    solution: {
      iAm: (persona) => `أنا ${persona}`,
      getStartedFree: 'ابدأ مجانًا',
      talkToSales: 'تحدّث مع المبيعات',
      overview: 'نظرة عامة',
      problemToday: 'المشكلة اليوم',
      whatDepositsCost: 'ما تكلّفك الودائع قبل Akara',
      whyTeams: 'لماذا تختار الفرق Akara',
      howItWorks: 'كيف يعمل',
      step: (n) => `الخطوة ${n}`,
      faqTitle: 'الأسئلة الشائعة',
      ctaSub: 'ابدأ خلال دقائق — دون أي التزام.',
    },
    faqPage: {
      label: 'الأسئلة الشائعة',
      title: 'الأسئلة الشائعة',
      subtitle: 'إجابات سريعة عن الأسئلة المتكررة حول الودائع وسير العمل والأمان.',
      stillTitle: 'لا تزال لديك أسئلة؟',
      stillText: 'يسعد فريقنا بإرشادك في أي شيء.',
      contactSupport: 'تواصل مع الدعم',
    },
    support: {
      label: 'الدعم',
      title: 'نحن هنا للمساعدة',
      subtitle:
        'فريقنا جاهز لمساعدتك على الانطلاق أسرع، وحل المشكلات، وتحسين سير عمل ودائعك.',
      liveChatTitle: 'الدردشة المباشرة',
      liveChatBody: 'تواصل مع فريق الدعم من الاثنين إلى الجمعة، 9 صباحًا – 6 مساءً (بتوقيت غرينتش +4).',
      emailTitle: 'الدعم عبر البريد الإلكتروني',
      emailBefore: 'أرسل التفاصيل إلى ',
      emailAfter: ' وسنرد خلال يوم عمل واحد.',
      disputesTitle: 'النزاعات ومشكلات الدفع',
      disputesBefore: 'لنزاعات الودائع أو مشكلات الدفع، راسلنا على ',
      disputesMid:
        '. في حالات ردّ المبالغ المدفوعة أو نزاعات الدفع قد نصعّد الأمر إلى مزوّد الدفع لدينا. راجع قسم ',
      disputesLink: 'التصعيد',
      disputesAfter: ' أدناه.',
      incidentTitle: 'الاستجابة للحوادث',
      incidentBody: 'استجابة ذات أولوية لمشكلات الدفع أو التوافر الحرجة.',
      onboardingTitle: 'الإعداد',
      onboardingBody: 'جلسات إعداد موجَّهة للفرق المنتقلة من الودائع اليدوية.',
      escalationTitle: 'التصعيد إلى مزوّد الدفع لدينا',
      escalationP1: 'نستخدم مزوّد دفع خارجيًا لمعالجة المدفوعات. وقد نصعّد إليه في حالات ',
      escalationChargebacks: 'ردّ المبالغ المدفوعة',
      escalationP2: ' (نزاع حامل البطاقة على عملية شراء مع بنكه) أو ',
      escalationDisputes: 'نزاعات الدفع',
      escalationP3: ' (تمييز عملية دفع للمراجعة). في تلك الحالات نتّبع إجراءات المزوّد؛ وقد يُطلب منك تقديم أدلة (مثل تأكيد التفويض وشروط الوديعة وسجلات الحجز). ويساعدنا الرد قبل أي موعد نحدّده لك على حل الحالة.',
      ctaTitle: 'تريد مراجعة تفاصيل المنتج أولًا؟',
      ctaText: 'يغطّي التوثيق لدينا الإعداد والتكاملات وأفضل الممارسات.',
      ctaBtn: 'تصفّح التوثيق',
    },
    contact: {
      label: 'تواصل معنا',
      title: 'تواصل معنا',
      subtitle: 'يسعدنا مساعدتك. تواصل مع فريقنا للإعداد أو أسئلة المنتج أو الدعم.',
      uae: 'الإمارات العربية المتحدة',
      callUs: 'اتصل بنا: ',
      emailUs: 'راسلنا: ',
      viewSupport: 'عرض موارد الدعم',
      sendTitle: 'أرسل لنا رسالة',
      sendText: 'أخبرنا بما تحتاجه وسيعاود فريقنا التواصل خلال يوم عمل واحد.',
      fullName: 'الاسم الكامل',
      companyName: 'اسم الشركة',
      workEmail: 'البريد الإلكتروني للعمل',
      message: 'الرسالة',
      sendBtn: 'إرسال الرسالة',
      officeLocations: 'مواقع المكاتب',
      dubaiHq: 'دبي (المقر الرئيسي)',
      dubaiAddr: 'دبي، الإمارات العربية المتحدة',
      preferBefore: 'تفضّل البريد الإلكتروني؟ راسلنا على ',
      preferAfter: '.',
    },
    pricing: {
      label: 'الأسعار',
      title: 'أسعار مصمَّمة للفرق الحديثة',
      subtitle:
        'ابدأ مجانًا، ثم ارتقِ مع نموّك. لا تدفع سوى رسم صغير عند المطالبة بوديعة — أما الودائع المُحرَّرة فمجانية دائمًا.',
      chooseTitle: 'اختر الخطة التي تناسب حجمك',
      chooseSub: 'كل خطة تشمل الطلبات المؤتمتة وبيانات دفع آمنة وسجلّات تدقيق.',
      loadingPlans: 'جارٍ تحميل الخطط…',
      mostPopular: 'الأكثر شيوعًا',
      yourPlan: 'خطتك',
      letsTalk: 'لنتحدّث',
      volumeBased: 'تسعير حسب الحجم · أدنى سعر للمطالبة لديك',
      free: 'مجانًا',
      noMonthlyFee: (pct) => `بلا رسم شهري · ${pct} لكل وديعة مُطالَب بها`,
      perMonth: (pct) => `شهريًا · ${pct} لكل وديعة مُطالَب بها`,
      talkToSales: 'تحدّث مع المبيعات',
      getStartedFree: 'ابدأ مجانًا',
      getStarted: 'ابدأ الآن',
      currentPlan: 'خطتك الحالية',
      requested: 'تم الطلب',
      requesting: 'جارٍ الطلب…',
      requestPlan: 'اطلب الخطة',
      requestedToast: (name) => `تم طلب خطة ${name}. سيؤكّد فريقنا قريبًا.`,
      requestError: 'تعذّر إرسال طلب خطتك.',
      featClaimFee: (pct) => `رسم منصة ${pct} لكل وديعة مُطالَب بها`,
      featAutomated: 'طلبات وتذكيرات مؤتمتة',
      featUpTo: (money) => `حتى ${money} لكل وديعة`,
      featNoLimit: 'بلا حد لكل وديعة',
      featEmail: 'إشعارات بريد إلكتروني',
      featSms: 'إشعارات رسائل نصية',
      featFraud: 'مراقبة الاحتيال وسجلّات التدقيق',
      featBranding: 'علامة تجارية مخصّصة',
      featApi: 'وصول لواجهة البرمجة + ويب هوك',
      featOnboarding: 'إعداد مخصّص واتفاقية مستوى خدمة',
      featLowestRate: 'أدنى سعر للمطالبة لديك',
      transparentLabel: 'تسعير شفّاف',
      transparentTitle: 'اعرف بالضبط ما تدفعه',
      transparentSub:
        'بلا رسوم خفية. تدفع خطتك الشهرية ورسم مطالبة فقط عند احتفاظك بوديعة — ولا شيء عند تحريرها.',
      comparedTitle: 'مقارنةً بالمعالجة التقليدية للودائع',
      traditionalTitle: 'الطرق التقليدية',
      traditionalItems: [
        'نقد محتجز لدى الوكلاء أو مديري العقارات.',
        'استردادات وتسويات يدوية.',
        'نزاعات تُعالَج غالبًا خارج النظام.',
        'شفافية منخفضة للمستأجرين.',
      ],
      akaraItems: [
        'ودائع رقمية مؤمَّنة دون حجز الأموال.',
        'تدفّقات تحرير مؤتمتة.',
        'تقارير جاهزة للتدقيق وسجلّات امتثال.',
        'تتم تسوية الأموال مباشرةً إلى حساب صرفك المتصل الخاص.',
      ],
      mech1Title: 'كيف يعمل التسعير',
      mech1Body:
        'تدفع خطة شهرية لفئتك، إضافةً إلى رسم منصة صغير على كل وديعة تطالب بها. وحرّر وديعةً فلا يكون هناك أي رسم على الإطلاق.',
      mech2Title: 'أسعار أقل مع نموّك',
      mech2Body:
        'تخفّض الفئات الأعلى رسم المطالبة لديك — من 3.9% في خطة Starter وصولًا إلى أدنى سعر تتفاوض عليه في خطة Custom — وترفع حجمك الشهري.',
      mech3Title: 'ما المتضمَّن',
      mech3Body:
        'كل خطة تشمل تذكيرات مؤتمتة ومراقبة احتيال وسجلّات تدقيق تبسّط النزاعات.',
      ctaTitle: 'تحتاج عرض سعر مخصّص؟',
      ctaText: 'تواصل معنا وسنبني خطة تناسب حجمك وسير عملك تحديدًا.',
    },
    auth: {
      needHelp: 'بحاجة إلى مساعدة؟',
      emailAddress: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      or: 'أو',
      redirecting: 'جارٍ إعادة التوجيه…',
      opening: 'جارٍ الفتح…',
      googleSignIn: 'تسجيل الدخول عبر Google',
      googleSignUp: 'إنشاء حساب عبر Google',
      appleSignIn: 'تسجيل الدخول عبر Apple',
      appleSignUp: 'إنشاء حساب عبر Apple',
      holdShow: 'اضغط مع الاستمرار لإظهار كلمة المرور',
      releaseHide: 'أفلت لإخفاء كلمة المرور',
      showcase: {
        signin: { eyebrow: 'بوابة التاجر', title: 'مرحبًا بعودتك إلى مركز عملياتك', description: 'تتبّع طلبات الودائع، ووافق على الإجراءات أسرع، وحافظ على سلاسة رحلة عميلك من لوحة واحدة.' },
        register: { eyebrow: 'ابدأ الآن', title: 'أطلق تدفّقات ودائعك خلال دقائق', description: 'أنشئ حساب التاجر وابدأ بإرسال طلبات آمنة تحمل علامتك مع رؤية الحالة في الوقت الفعلي.' },
        forgot: { eyebrow: 'استعادة الحساب', title: 'استعد وصولك بأمان', description: 'أعد تعيين كلمة مرورك بسرعة وواصل إدارة طلباتك دون فقدان الزخم.' },
        verify: { eyebrow: 'التحقّق', title: 'خطوة واحدة متبقية لتفعيل حسابك', description: 'أكّد بريدك الإلكتروني لتأمين حسابك وفتح مساحة عمل التاجر الكاملة.' },
        'forgot-sent': { eyebrow: 'تم إرسال البريد', title: 'تحقّق من بريدك للحصول على رابط إعادة التعيين', description: 'أرسلنا تعليمات إعادة تعيين كلمة المرور لتتمكّن من العودة ومتابعة ما بدأته.' },
      },
      showcasePoints: ['حالة الودائع في الوقت الفعلي', 'نزاعات ومتابعات أقل', 'إعداد سريع لفرق الإمارات'],
      tabSignIn: 'تسجيل الدخول',
      tabCreate: 'إنشاء حساب',
      welcomeBack: 'مرحبًا بعودتك',
      signinSub: 'سجّل الدخول للوصول إلى لوحة التاجر وإدارة الودائع.',
      signinBtn: 'تسجيل الدخول',
      signingIn: 'جارٍ تسجيل الدخول…',
      forgotLink: 'نسيت كلمة المرور؟',
      enterPassword: 'أدخل كلمة المرور',
      createTitle: 'أنشئ حسابك',
      createSub: 'سجّل ببريدك الإلكتروني للبدء. سنرسل رابط تحقّق لتأكيد عنوانك.',
      confirmPassword: 'تأكيد كلمة المرور',
      min6: '6 أحرف كحد أدنى',
      reenter: 'أعد إدخال كلمة المرور',
      agreeTermsBefore: 'أوافق على ',
      termsLink: 'شروط وأحكام الاستخدام',
      agreeTermsAfter: ' الخاصة بـ Akara.',
      agreePrivacyBefore: 'لقد قرأت وفهمت ',
      privacyLink: 'سياسة حماية البيانات الشخصية',
      agreePrivacyAfter: ' الخاصة بـ Akara.',
      createBtn: 'إنشاء حساب',
      creating: 'جارٍ إنشاء الحساب…',
      checkEmailTitle: 'تحقّق من بريدك الإلكتروني',
      checkEmailBefore: 'أرسلنا رابط تحقّق إلى ',
      checkEmailAfter: '. انقر الرابط في بريدك للتحقّق من حسابك وإكمال إعداد ملفك.',
      backToSignin: 'العودة إلى تسجيل الدخول',
      didntBefore: 'لم يصلك؟ تحقّق من البريد العشوائي أو ',
      tryAgain: 'حاول مرة أخرى',
      didntAfter: '.',
      resetTitle: 'أعد تعيين كلمة مرورك',
      resetSub: 'أدخل بريدك الإلكتروني وسنرسل لك رابطًا لإعادة تعيين كلمة مرورك.',
      sendResetBtn: 'إرسال رابط إعادة التعيين',
      sending: 'جارٍ الإرسال…',
      resetSentTitle: 'تم إرسال رابط إعادة التعيين',
      resetSentBefore: 'أرسلنا رابط إعادة تعيين كلمة المرور إلى ',
      resetSentAfter: '. انقر الرابط في بريدك لتعيين كلمة مرور جديدة.',
      errEmailPassword: 'يرجى إدخال بريدك الإلكتروني وكلمة مرورك.',
      errPasswordMin: 'يجب أن تتكوّن كلمة المرور من 6 أحرف على الأقل.',
      errPasswordMatch: 'كلمتا المرور غير متطابقتين.',
      errAcceptTerms: 'يرجى قبول الشروط والأحكام وسياسة الخصوصية.',
      errEmail: 'يرجى إدخال بريدك الإلكتروني.',
      setNewTitle: 'تعيين كلمة مرور جديدة',
      setNewSub: 'اختر كلمة مرور جديدة لحسابك.',
      newPassword: 'كلمة المرور الجديدة',
      confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
      updateBtn: 'تحديث كلمة المرور',
      updating: 'جارٍ التحديث…',
      passwordUpdatedTitle: 'تم تحديث كلمة المرور',
      passwordUpdatedBody: 'تمت إعادة تعيين كلمة مرورك بنجاح. جارٍ إعادة التوجيه إلى لوحتك…',
    },
    getStarted: {
      headerTitle: 'ابدأ مجانًا',
      headerSub: 'أخبرنا عن نشاطك التجاري لنجهّز حساب التاجر الخاص بك.',
      successTitle: 'شكرًا! سنتواصل معك قريبًا.',
      successBody: 'سيراجع فريقنا تفاصيلك ويتواصل معك خلال يوم عمل واحد. وإذا كان لديك حساب بالفعل، سجّل الدخول للمتابعة.',
      signIn: 'تسجيل الدخول',
      submitAnother: 'إرسال رد آخر',
      verifyTitle: 'تحقّق من رقم هاتفك',
      verifyBefore: 'أرسلنا رمزًا من 6 أرقام إلى ',
      verifyAfter: '. أدخله أدناه لإكمال إعداد حساب التاجر الخاص بك.',
      verifyCodeLabel: 'رمز التحقّق',
      verifyBtn: 'تحقّق وتابع',
      verifying: 'جارٍ التحقّق…',
      resendBtn: 'إعادة إرسال الرمز',
      resending: 'جارٍ إعادة الإرسال…',
      resendIn: (s) => `إعادة الإرسال خلال ${s} ث`,
      lobLabel: 'مجال عملي',
      lobPlaceholder: 'اختر مجال العمل',
      lobPropertyManagement: 'إدارة العقارات',
      lobVehicleRental: 'تأجير المركبات',
      lobEquipmentRental: 'تأجير المعدات',
      lobHealthcare: 'الرعاية الصحية',
      lobOther: 'أخرى',
      currencyLabel: 'عملة حسابي',
      businessNameLabel: 'اسم نشاطي التجاري',
      websiteLabel: 'موقعي الإلكتروني',
      countryLabel: 'الدولة',
      countryUae: 'الإمارات العربية المتحدة',
      countrySaudi: 'المملكة العربية السعودية',
      countryQatar: 'قطر',
      countryKuwait: 'الكويت',
      cityLabel: 'المدينة',
      firstNameLabel: 'الاسم الأول',
      lastNameLabel: 'اسم العائلة',
      emailLabel: 'بريدي الإلكتروني',
      phoneLabel: 'رقم هاتفي',
      promoLabel: 'رمز ترويجي',
      agreeTermsBefore: 'أقبل ',
      termsLink: 'شروط وأحكام الاستخدام',
      agreeTermsAfter: ' الخاصة بـ Akara.',
      agreePrivacyBefore: 'لقد قرأت وفهمت ',
      privacyLink: 'سياسة حماية البيانات الشخصية',
      agreePrivacyAfter: ' الخاصة بـ Akara.',
      submitBtn: 'إكمال التسجيل',
      saving: 'جارٍ الحفظ…',
      errAcceptTerms: 'يرجى قبول الشروط والأحكام وسياسة الخصوصية.',
      errOtp: 'أدخل الرمز الذي أرسلناه إلى هاتفك.',
      errMissing: (label) => `يرجى إدخال ${label}.`,
      fieldBusinessName: 'اسم نشاطك',
      fieldCity: 'مدينتك',
      fieldFirstName: 'اسمك الأول',
      fieldLastName: 'اسم عائلتك',
      fieldEmail: 'بريدك الإلكتروني',
      fieldPhone: 'رقم هاتفك',
    },
    waitlist: {
      brandEyebrow: 'انضم إلى قائمة الانتظار',
      brandTitle: 'الودائع، بلا عناء أخيرًا.',
      depositHeld: 'الوديعة محجوزة',
      active: 'نشِطة',
      releases: 'تُحرَّر خلال 3 أيام لا 10',
      stepIndustry: 'قطاعك',
      stepVolume: 'حجم الودائع',
      stepCompany: 'تفاصيل النشاط',
      stepContact: 'معلومات التواصل',
      stepReview: 'المراجعة والانضمام',
      industries: {
        shortterm: { label: 'الإيجارات قصيرة الأجل', desc: 'Airbnb وVrbo والحجوزات المباشرة' },
        longterm: { label: 'الملّاك والإيجارات طويلة الأجل', desc: 'ودائع عقود الإيجار السكنية' },
        car: { label: 'تأجير السيارات والمركبات', desc: 'أساطيل التأجير اليومي والأسبوعي' },
        equipment: { label: 'تأجير المعدات', desc: 'الأدوات والعتاد والآلات' },
        healthcare: { label: 'الرعاية الصحية', desc: 'ودائع طبية وودائع الأسنان' },
        other: { label: 'أخرى', desc: 'أي نشاط يأخذ ودائع' },
      },
      metricLabels: {
        'Number of listings': 'عدد القوائم',
        'Average deposit held': 'متوسط الوديعة المحجوزة',
        'Frequency of bookings': 'وتيرة الحجوزات',
        'Units managed': 'عدد الوحدات المُدارة',
        'Average deposit per tenancy': 'متوسط الوديعة لكل عقد إيجار',
        'Typical tenancy length': 'المدة المعتادة للإيجار',
        'Fleet size': 'حجم الأسطول',
        'Average deposit per rental': 'متوسط الوديعة لكل تأجير',
        'Typical rental length': 'المدة المعتادة للتأجير',
        'Items in inventory': 'عدد العناصر في المخزون',
        'Average deposit per item': 'متوسط الوديعة لكل عنصر',
        'Typical rental frequency': 'وتيرة التأجير المعتادة',
        'Patients per month': 'عدد المرضى شهريًا',
        'Average deposit / copay held': 'متوسط الوديعة / المشاركة المحجوزة',
        'Deposit release trigger': 'مُطلِق تحرير الوديعة',
      },
      options: {
        'Under 10 / month': 'أقل من 10 / شهريًا', '10–25 / month': '10–25 / شهريًا', '26–50 / month': '26–50 / شهريًا', '51–100 / month': '51–100 / شهريًا', '100+ / month': '100+ / شهريًا',
        'Month-to-month': 'شهريًا بالتجديد', '6 months': '6 أشهر', '12 months': '12 شهرًا', '24+ months': '24+ شهرًا',
        '1–2 days': '1–2 يوم', '3–7 days': '3–7 أيام', '1–4 weeks': '1–4 أسابيع', '1+ month': 'شهر فأكثر',
        Daily: 'يوميًا', Weekly: 'أسبوعيًا', Monthly: 'شهريًا',
        'Visit complete': 'اكتمال الزيارة', 'Insurance clears': 'تسوية التأمين', 'Manual review': 'مراجعة يدوية',
      },
      countries: {
        'United Arab Emirates': 'الإمارات العربية المتحدة', 'Saudi Arabia': 'المملكة العربية السعودية', Qatar: 'قطر', Kuwait: 'الكويت', Bahrain: 'البحرين', Oman: 'عُمان',
      },
      selectOption: 'اختر خيارًا',
      industryTitle: 'ما نوع الودائع التي تأخذها؟',
      industrySub: 'اختر ما يناسب نشاطك أكثر.',
      volumeTitle: 'أخبرنا عن حجمك',
      volumeSub: 'التقديرات التقريبية كافية — تخطَّ ما لست متأكدًا منه.',
      companyTitle: 'عن نشاطك التجاري',
      companySub: 'حتى نجهّز الحساب المناسب عند وصول دعوتك.',
      businessName: 'اسم النشاط التجاري',
      website: 'الموقع الإلكتروني',
      optional: '(اختياري)',
      country: 'الدولة',
      city: 'المدينة',
      contactTitle: 'إلى أين نرسل دعوتك؟',
      contactSub: 'سنراسلك عبر البريد بشأن الوصول المبكر فقط — بلا إزعاج.',
      fullName: 'الاسم الكامل',
      workEmail: 'البريد الإلكتروني للعمل',
      emailError: 'أدخل بريدًا إلكترونيًا صالحًا مثل name@company.com',
      phone: 'الهاتف',
      reviewTitle: 'هل كل شيء صحيح؟',
      reviewSub: 'أكّد تفاصيلك واحجز مكانك في قائمة الانتظار.',
      rowIndustry: 'القطاع',
      rowBusiness: 'النشاط',
      rowLocation: 'الموقع',
      rowContact: 'جهة الاتصال',
      rowEmail: 'البريد الإلكتروني',
      dash: '—',
      there: 'صديقنا',
      doneTitle: (n) => `أنت الآن على القائمة، ${n}.`,
      doneBefore: 'سنراسلك على ',
      doneAfter: (industry) => ` فور فتح الوصول المبكر لأنشطة ${industry}.`,
      addAnother: 'إضافة نشاط آخر',
      back: 'رجوع',
      continueBtn: 'متابعة',
      join: 'انضم إلى قائمة الانتظار',
      joining: 'جارٍ الانضمام…',
      submitError: 'حدث خطأ أثناء حفظ تفاصيلك. يرجى المحاولة مرة أخرى.',
    },
    onboarding: {
      back: 'رجوع',
      stepOf: (n, total) => `الخطوة ${n} من ${total}`,
      continueBtn: 'متابعة',
      finish: 'إنهاء الإعداد',
      saving: 'جارٍ الحفظ…',
      loading: 'جارٍ التحميل…',
      selectOption: 'اختر خيارًا',
      welcomeTitle: 'لنُجهّز مساحة عمل Akara الخاصة بك',
      welcomeSub: 'أخبرنا عن نشاطك التجاري وسنهيّئ لوحة تناسب طريقة أخذك للودائع — يستغرق الأمر نحو 3 دقائق.',
      proof1: 'أموال مُفوَّضة مسبقًا، دون حجب أبدًا',
      proof2: 'تُحرَّر خلال أيام لا أسابيع',
      proof3: 'كل وديعة متتبَّعة وموثّقة',
      getStarted: 'ابدأ الآن',
      personaTitle: 'من الذي يقوم بالإعداد؟',
      personaSub: 'هذا يحدّد الحقول التي نسألك عنها بعد ذلك.',
      businessTitle: 'شركة',
      businessDesc: 'شركة تدير الودائع عبر العقارات أو الأساطيل أو العملاء',
      individualTitle: 'فرد',
      individualDesc: 'مالك أو مضيف يدير الودائع بنفسه',
      industriesTitle: 'لأي قطاعات تأخذ ودائع؟',
      industriesSub: 'اختر كل ما ينطبق — يخصّص هذا لوحتك وتدفّقات العمل الافتراضية.',
      metricsTitle: 'أخبرنا عن حجمك',
      metricsSub: 'التقديرات التقريبية كافية — هذا يضبط قيمًا افتراضية معقولة فقط. يمكنك تخطّي أي شيء.',
      profileTitle: 'بعض التفاصيل عنك',
      profileSub: 'سنستخدم هذا لإعداد حسابك وإرسال إشعارات الودائع.',
      fullName: 'الاسم الكامل',
      workEmail: 'البريد الإلكتروني للعمل',
      emailError: 'أدخل بريدًا إلكترونيًا صالحًا (مثل name@company.com)',
      companyName: 'اسم الشركة',
      verifyTitle: 'تحقّق من بريدك',
      verifyBefore: 'أرسلنا رمزًا من 6 أرقام إلى ',
      yourEmail: 'بريدك',
      resendCode: 'إعادة إرسال الرمز',
      resending: 'جارٍ إعادة الإرسال…',
      codeResent: 'تمت إعادة إرسال الرمز.',
      dashboardTitle: 'اضبط لوحتك',
      dashboardSub: 'الإعدادات الافتراضية الشائعة مفعّلة بالفعل — عدّل ما تشاء قبل الدخول إلى Akara.',
      notifications: 'الإشعارات',
      integrations: 'التكاملات',
      currencyDefaults: 'العملة والقيم الافتراضية للوديعة',
      defaultCurrency: 'العملة الافتراضية',
      defaultDeposit: 'مبلغ الوديعة الافتراضي',
      successTitle: (name) => `كل شيء جاهز، ${name}`,
      successBefore: 'تم تهيئة مساحة عمل ',
      yourCompany: 'شركتك',
      successAfter: '. إليك ما أعددناه:',
      accountType: 'نوع الحساب',
      industriesRow: 'القطاعات',
      noneSelected: 'لم يتم اختيار شيء',
      goToDashboard: 'الذهاب إلى اللوحة',
      there: 'صديقنا',
      personaBusiness: 'شركة',
      personaIndividual: 'فرد',
      dash: '—',
      errSaveDetails: 'تعذّر حفظ تفاصيلك.',
      errSaveNotif: 'تعذّر حفظ تفضيلات الإشعارات.',
      errSaveDefaults: 'تعذّر حفظ الإعدادات الافتراضية للوحتك.',
      industries: {
        shortterm: { label: 'بيوت العطلات والإيجارات قصيرة الأجل', desc: 'Airbnb وVrbo والحجوزات المباشرة' },
        longterm: { label: 'المالك والمستأجر', desc: 'عقود إيجار سكنية طويلة الأجل' },
        car: { label: 'تأجير السيارات', desc: 'تأجير المركبات يوميًا وأسبوعيًا' },
        equipment: { label: 'تأجير المعدات', desc: 'الأدوات والعتاد والآلات' },
        healthcare: { label: 'الرعاية الصحية', desc: 'ودائع طبية وودائع الأسنان' },
      },
      metricLabels: {
        'Number of listings': 'عدد القوائم',
        'Average nightly deposit': 'متوسط الوديعة الليلية',
        'Primary booking platform': 'منصة الحجز الأساسية',
        'Number of units managed': 'عدد الوحدات المُدارة',
        'Average deposit per tenancy': 'متوسط الوديعة لكل عقد إيجار',
        'Average tenancy length': 'متوسط مدة الإيجار',
        'Fleet size': 'حجم الأسطول',
        'Average deposit per rental': 'متوسط الوديعة لكل تأجير',
        'Typical rental length': 'المدة المعتادة للتأجير',
        'Items in inventory': 'عدد العناصر في المخزون',
        'Average deposit per item': 'متوسط الوديعة لكل عنصر',
        'Typical rental frequency': 'وتيرة التأجير المعتادة',
        'Patients per month': 'عدد المرضى شهريًا',
        'Average deposit / copay held': 'متوسط الوديعة / المشاركة المحجوزة',
        'Deposit release trigger': 'مُطلِق تحرير الوديعة',
      },
      options: {
        Airbnb: 'Airbnb', 'Booking.com': 'Booking.com', Vrbo: 'Vrbo', 'Direct / own site': 'مباشر / موقعي الخاص', Other: 'أخرى',
        'Month-to-month': 'شهريًا بالتجديد', '6 months': '6 أشهر', '12 months': '12 شهرًا', '24+ months': '24+ شهرًا',
        '1-2 days': '1–2 يوم', '3-7 days': '3–7 أيام', '1-4 weeks': '1–4 أسابيع', '1+ month': 'شهر فأكثر',
        Daily: 'يوميًا', Weekly: 'أسبوعيًا', Monthly: 'شهريًا',
        'Visit complete': 'اكتمال الزيارة', 'Insurance clears': 'تسوية التأمين', 'Manual review': 'مراجعة يدوية',
      },
      notif: {
        received: { label: 'استلام وديعة', desc: 'عند تأمين وديعة جديدة' },
        nearing: { label: 'اقتراب تحرير الوديعة', desc: 'قبل 3 أيام من التحرير المجدول' },
        dispute: { label: 'فتح نزاع', desc: 'عند تقديم مطالبة أو نزاع' },
        weekly: { label: 'بريد الملخّص الأسبوعي', desc: 'ملخّص للنشاط كل يوم اثنين' },
      },
      integrationLater: 'سأربط لاحقًا',
    },
    admin: {
      badge: 'لوحة الإدارة',
      overview: 'نظرة عامة',
      platform: 'المنصة',
      merchants: 'التجار',
      deposits: 'الودائع',
      claims: 'المطالبات',
      escalations: 'التصعيدات',
      signOut: 'تسجيل الخروج',
      switchToMerchant: 'التبديل إلى عرض التاجر',
      superAdmin: 'مسؤول رئيسي',
      meta: {
        users: { title: 'التجار', description: 'جميع التجار المسجّلين ونشاطهم.' },
        deposits: { title: 'الودائع', description: 'أحدث طلبات الودائع عبر جميع التجار.' },
        claims: { title: 'المطالبات', description: 'راجع المطالبات المقدَّمة من التجار واتّخذ إجراءً بشأنها.' },
        home: { title: 'نظرة عامة للإدارة', description: 'لمحة شاملة عن التجار والودائع والمطالبات على مستوى المنصة.' },
      },
    },
  },
}

export const getUi = (locale) => UI[locale] || UI.en

// Convenience hook: current-locale UI strings.
export function useUi() {
  return getUi(useLocale().locale)
}
